<template>
  <div>
    <div class="w-80">
      <div class="flex flex-col p-3">
        <div class="flex justify-between gap-1">
          <div class="inline-flex items-center">
            <SymbolTradeStateIcon
              class="me-2"
              size="medium"
              :trading-state-code="order.instrumentTradingState"
            />

            <Retail :isin="order.instrumentIsin ?? ''">
              <NText variant="h3" tag="h3" weight="bold">
                {{ order.instrumentName }}
              </NText>
            </Retail>
          </div>

          <div class="flex flex-col justify-around">
            <div class="inline-flex items-center">
              <NumberFormatter
                #="{ number }"
                :value="(order.referencePriceVariationPercentage ?? 0) / 100"
                maximum-fraction-digits="2"
                sign-display="always"
                number-style="percent"
              >
                <NText
                  weight="bold"
                  dir="ltr"
                  class="mx-1"
                  :class="{
                    'text-brand-primary':
                      (order.referencePriceVariationPercentage ?? 0) > 0,
                    'text-critical-primary':
                      (order.referencePriceVariationPercentage ?? 0) < 0,
                  }"
                >
                  ({{ number }})
                </NText>
              </NumberFormatter>

              <NumberFormatter #="{ number }" :value="order.lastTradePrice">
                <ChangePriceEffect :price="order.lastTradePrice ?? 0">
                  <NText :key="number" variant="h1" tag="h1" weight="bold">
                    {{ number }}
                  </NText>
                </ChangePriceEffect>
              </NumberFormatter>
            </div>
          </div>
        </div>

        <NText variant="caption1" class="text-neutral-secondary">
          {{ order.instrumentTitle }}
        </NText>
      </div>
    </div>

    <div class="mt-4 p-4 bg-neutral-secondary rounded-md">
      <div class="flex justify-between items-center pb-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.requestNumber') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{
            t('modules.order.percentOfAsset', {
              percent: order.falconAlgorithmInfo?.percent ?? 0,
            })
          }}
        </NText>
      </div>

      <div class="flex justify-between items-center pb-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.profitLimit') }}
        </NText>

        <div v-if="order.falconAlgorithmInfo?.upperTrigger">
          <NumberFormatter
            #="{ number }"
            :value="order.falconAlgorithmInfo?.upperTrigger ?? 0"
          >
            <NText variant="caption1" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary pr-1">
            {{ t('misc.rial') }}
          </NText>
        </div>

        <div v-else>
          <NText variant="caption1" class="text-neutral-secondary"> - </NText>
        </div>
      </div>

      <div
        v-if="order.falconAlgorithmInfo?.upperTrigger"
        class="flex justify-between items-center pb-2"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.profitLimitOrderPrice') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ t('modules.order.priceType.marketPrice.title') }}
        </NText>
      </div>

      <div
        class="flex justify-between items-center"
        :class="{ 'pb-2': order.falconAlgorithmInfo?.lowerTrigger }"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.limitOfLoss') }}
        </NText>

        <div v-if="order.falconAlgorithmInfo?.lowerTrigger">
          <NumberFormatter
            #="{ number }"
            :value="order.falconAlgorithmInfo?.lowerTrigger ?? 0"
          >
            <NText variant="caption1" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary pr-1">
            {{ t('misc.rial') }}
          </NText>
        </div>

        <div v-else>
          <NText variant="caption1" class="text-neutral-secondary"> - </NText>
        </div>
      </div>

      <div
        v-if="order.falconAlgorithmInfo?.lowerTrigger"
        class="flex justify-between items-center pb-2 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.limitOfLossOrderPrice') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ t('modules.order.priceType.marketPrice.title') }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.creationTime') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ creationTime(order.falconAlgorithmInfo?.createdOn) }}
        </NText>
      </div>

      <div class="flex justify-between items-center">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{
            order.falconAlgorithmInfo?.expirationDate
              ? d(order.falconAlgorithmInfo?.expirationDate, 'shortDate')
              : '-'
          }}
        </NText>
      </div>
    </div>

    <NButton
      ref="deleteOrderButtonRef"
      variant="outline"
      color="red"
      class="w-full mt-6"
      size="small"
      @click="deleteOrderDialog"
    >
      <NText variant="caption1" weight="medium">
        {{ t('modules.order.cancelOrder') }}
      </NText>
    </NButton>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { PortfolioItem } from '@libs/api-reference';
import { useI18n } from 'vue-i18n';
import { NText, useDialog, useToast } from '@negar/core';
import { useDeleteOfflineOrderQuery } from '@core/order';
import { PORTFOLIO_QUERY_KEYS } from '@core/portfolio';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

import { Retail, SymbolTradeStateIcon } from '@/modules/symbol';
import { DeleteOrderDialogFooter, SLTP_DETAIL } from '@/modules/order';

type SLTPPortfolioDetailProps = {
  instrumentName: string;
  order: PortfolioItem;
  from: (typeof SLTP_DETAIL)[keyof typeof SLTP_DETAIL];
};

const props = defineProps<SLTPPortfolioDetailProps>();

const { add: addDialog, removeAll: removeAllDialog } = useDialog();
const toast = useToast();

const queryClient = useQueryClient();

const { t, d } = useI18n();

const deleteOrderButtonRef = ref<HTMLElement>();

const { deleteOfflineOrder, isLoadingDeleteOfflineOrder } =
  useDeleteOfflineOrderQuery({
    async onSuccess(result, { closeDeleteOrderDialog }) {
      toast.neutral({
        message: t('modules.order.cancelOrderMessage'),
        duration: 3000,
      });

      await sleep(1000);

      queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.portfolio);

      removeAllDialog();

      closeDeleteOrderDialog();
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const creationTime = (date) =>
  `${d(date, 'shortDate')} ,
    ${d(date, 'shortTime')}`;

const deleteOrderDialog = () => {
  addDialog({
    bodyClass: '!w-1/5',
    title: t('modules.order.cancelOrder'),
    content: t('modules.order.cancelOrderQuestion', {
      instrumentName: props.instrumentName,
      orderSide: t('common.sell'),
    }),
    referenceElementRef: deleteOrderButtonRef,
    placement: 'top',
    footer: computed(() => ({
      component: DeleteOrderDialogFooter,
      props: {
        order: props.order,
        isFromPortfolio: true,
        loadingDeleteOrder: isLoadingDeleteOfflineOrder.value,
        onDeleteOrder: deleteOfflineOrder,
      },
    })),
  });
};
</script>
